/**
 * /ai/reasoning — Session 3a PR 19.
 *
 * Activates the previously-placeholder route. Now renders the
 * reasoning-trace store from the M3X.1 v2 agent_reasoning_traces
 * table with the hash-chain integrity verifier.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Mutaba'aa brand)
 *   2. InlineAISuggestion cards (confidence drift · threshold raise)
 *   3. TraceableValue wrapping confidence + plan_step_count +
 *      tool_call_count (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ShieldCheck, TrendingUp, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { AgentBadge } from "../components/ui/AgentBadge";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type ReasoningTraceItem,
} from "../lib/session3a-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "./_session3aHelpers";

type AgentFilter = "all" | "tashghil" | "rawatib" | "mutabaa" | "khidmat";

export function AIReasoningTracesPage() {
  const { t } = useTranslation();
  const [agent, setAgent] = useState<AgentFilter>("all");
  const [days, setDays] = useState<number>(1);
  const [confFilter, setConfFilter] = useState<"all" | "low_conf" | "escalated">(
    "all",
  );

  const list = useQuery({
    queryKey: ["reasoning-traces", agent, days],
    queryFn: () =>
      session3aApi.listReasoningTraces({
        agent: agent === "all" ? undefined : agent,
        days,
      }),
  });

  const items: ReasoningTraceItem[] = list.data?.items ?? [];
  const chainIntact = list.data?.chain_intact ?? true;

  const stats = useMemo(() => {
    const escalated = items.filter((r) => r.outcome === "escalated").length;
    const lowConf = items.filter((r) => r.confidence < 0.55).length;
    const avgConf =
      items.length > 0
        ? items.reduce((acc, r) => acc + r.confidence, 0) / items.length
        : 0;
    const broken = items.filter((r) => !r.hash_chain_verified).length;
    return {
      total: items.length,
      escalated,
      lowConf,
      avgConf,
      broken,
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (confFilter === "low_conf") return items.filter((r) => r.confidence < 0.55);
    if (confFilter === "escalated") return items.filter((r) => r.outcome === "escalated");
    return items;
  }, [items, confFilter]);

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("ai.reasoning.title", "AI Reasoning Traces")}
          subtitle={t(
            "ai.reasoning.subtitle",
            "Every agent decision writes a trace per commandment 68. Cryptographically chained per (tenant, agent); each row carries goal · plan · tool calls · outcome · confidence.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["all", "All"],
                ["tashghil", "Tashghil"],
                ["rawatib", "Rawatib"],
                ["mutabaa", "Mutaba'aa"],
                ["khidmat", "Khidmat"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setAgent(key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  agent === key
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`ai.reasoning.filter.${key}`, label)}
              </button>
            ))}
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  days === d
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`ai.reasoning.filter.days_${d}`, d === 1 ? "24h" : `${d}d`)}
              </button>
            ))}
            <button
              type="button"
              onClick={() =>
                setConfFilter(confFilter === "low_conf" ? "all" : "low_conf")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                confFilter === "low_conf"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("ai.reasoning.filter.low_conf", "Low confidence (<0.55)")}
            </button>
            <button
              type="button"
              onClick={() =>
                setConfFilter(confFilter === "escalated" ? "all" : "escalated")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                confFilter === "escalated"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("ai.reasoning.filter.escalated", "Escalated")}
            </button>
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "ai.reasoning.verify_chain_note",
                  `Re-hashes every reasoning trace per (tenant, agent). Result: ${chainIntact ? "Verified ✓" : "Chain broken ✗"}. Wiring: GET /api/audit/agent-events/chain/verify.`,
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <ShieldCheck className="h-4 w-4" />
            {t("ai.reasoning.cta.verify", "Verify chain integrity")}
          </button>
        </div>

        <div
          className={`rounded-2xl border p-3 ${
            chainIntact
              ? "border-emerald-300 bg-emerald-50"
              : "border-rose-300 bg-rose-50"
          }`}
        >
          <div className="flex items-center gap-2 text-sm">
            {chainIntact ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-700" />
            ) : (
              <XCircle className="h-4 w-4 text-rose-700" />
            )}
            <span className={chainIntact ? "text-emerald-800" : "text-rose-800"}>
              {chainIntact
                ? t(
                    "ai.reasoning.chain_intact",
                    "Hash chain verified — every reasoning trace is tamper-evident.",
                  )
                : t(
                    "ai.reasoning.chain_broken",
                    "Hash chain broken — escalate immediately.",
                  )}
            </span>
          </div>
        </div>

        {stats.avgConf < 0.7 && stats.total > 5 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<TrendingUp className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "ai.reasoning.suggestion.drift_headline",
              `Confidence drift: average ${stats.avgConf.toFixed(2)} across recent traces.`,
            )}
            body={t(
              "ai.reasoning.suggestion.drift_body",
              "Investigate — may signal prompt drift, model degradation, or harder edge cases hitting the agent.",
            )}
            primaryAction={{
              label: t(
                "ai.reasoning.suggestion.see_low_conf",
                "See low-confidence",
              ),
              onClick: () => setConfFilter("low_conf"),
            }}
            suggestionKey={`reasoning-drift-${stats.avgConf.toFixed(2)}`}
          />
        )}
        {stats.avgConf > 0.85 && stats.total > 20 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<TrendingUp className="h-4 w-4" />}
            severity="info"
            headline={t(
              "ai.reasoning.suggestion.raise_headline",
              `Confidence consistently above 0.85 — threshold could be tuned higher.`,
            )}
            body={t(
              "ai.reasoning.suggestion.raise_body",
              "Higher auto-resolve threshold would handle more cases autonomously without sacrificing trust.",
            )}
            primaryAction={{
              label: t(
                "ai.reasoning.suggestion.open_thresholds",
                "Open behaviour thresholds",
              ),
              onClick: () => window.location.assign("/ai/behavior"),
            }}
            suggestionKey={`reasoning-raise-${stats.avgConf.toFixed(2)}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("ai.reasoning.col.timestamp", "Timestamp")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.agent", "Agent")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.decision", "Decision")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.entity", "Entity")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.goal", "Goal pursued")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.plan", "Plan")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.tools", "Tools")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.outcome", "Outcome")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.confidence", "Confidence")}</th>
                  <th className="px-3 py-2">{t("ai.reasoning.col.mode", "Mode")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.trace_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.emitted_at.slice(11, 19)}</span>
                    </td>
                    <td className="px-3 py-2">
                      <AgentBadge agent={row.agent} variant="compact" />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <a
                        href={`/ai/decision-registry?decision_id=${row.decision_id}`}
                        className="text-[#2260C9] hover:underline"
                      >
                        {row.decision_id}
                      </a>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span className="text-text-tertiary">{row.entity_type}:</span>{" "}
                      {row.entity_id.slice(0, 8)}…
                    </td>
                    <td
                      className="px-3 py-2 text-xs text-text-secondary"
                      title={row.goal_pursued}
                    >
                      {row.goal_pursued.length > 50
                        ? `${row.goal_pursued.slice(0, 50)}…`
                        : row.goal_pursued}
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.reasoning.trace.plan",
                              `Plan has ${row.plan_step_count} steps. Drawer surfaces the full plan + tool calls + outcomes once wiring lands.`,
                            ),
                          )
                        }
                      >
                        <span className="text-text-secondary">{row.plan_step_count}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.reasoning.trace.tools",
                              `${row.tool_call_count} tool calls in this decision. M3X.1 v2 captures each call's args + result in agent_reasoning_traces.tool_calls JSONB.`,
                            ),
                          )
                        }
                      >
                        <span className="text-text-secondary">{row.tool_call_count}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.outcome === "success"
                            ? "info"
                            : row.outcome === "escalated"
                              ? "attention"
                              : row.outcome === "failed"
                                ? "critical"
                                : "neutral",
                        )}`}
                      >
                        {row.outcome}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.reasoning.trace.confidence",
                              `confidence = ${row.confidence}. Below 0.55 always escalates per commandment 3; threshold per mode in Decision Registry.`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            row.confidence >= 0.85
                              ? "bg-emerald-100 text-emerald-800"
                              : row.confidence >= 0.55
                                ? "bg-amber-100 text-amber-800"
                                : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          {row.confidence.toFixed(2)}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.mode_at_decision}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "ai.reasoning.empty",
              "No reasoning traces in this window — agents may not be active or trace-store may need investigation.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: stats.total,
            attention: stats.escalated,
            critical: stats.lowConf + stats.broken,
            top_kind: t("ai.reasoning.title", "Reasoning Traces"),
            window_label: `last ${days}d`,
          }}
        />
      </aside>
    </div>
  );
}
